import { GradingData } from "../../../components/adminDashboard/GradingCenter/GradingData";
import { StyleSheet, View, Text, Platform } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomAccordion from "../../../components/common/general/CustomAccordion/CustomAccordion";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";

import { ActivityIndicator } from "react-native";
import React from "react";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { UpdatedEntityType } from "../../../graphql/generated/graphql";
import { GradeTree } from "./types";

const SubmissionAndGradingTab = ({
  gradeTree,
  gradingData,
  isLoading,
  handleGradeChange,
  canEditScore,
  handleSave,
}: {
  gradeTree: GradeTree;
  gradingData: GradingData | null;
  isLoading: boolean;
  handleGradeChange: ({
    updatedEntityType,
    updatedEntityId,
    updatedGrade,
    maxPointsForEntity,
  }: {
    updatedEntityType: UpdatedEntityType;
    updatedEntityId: string;
    updatedGrade: string;
    maxPointsForEntity: number;
  }) => Promise<void>;
  canEditScore: boolean;
  handleSave: (closeModal: boolean) => Promise<void>;
}) => {
  const calculateTimeSpent = () => {
    const startedAt = new Date(gradingData?.startedAt || "");
    const completedAt = new Date(gradingData?.completedAt || "");
    const timeSpent = completedAt.getTime() - startedAt.getTime();
    const minutes = Math.floor(timeSpent / (1000 * 60));

    if (Number.isNaN(minutes)) {
      return "not available";
    }

    return `${minutes} min`;
  };

  return (
    <>
      <CustomSpacing size="xl" />
      <View style={styles.paddingContainer}>
        {canEditScore ? (
          <CustomText
            text="Review the student's responses, how they were graded, and any manual adjustments made."
            style={{ color: "#667085" }}
            weight="normal"
          />
        ) : (
          <CustomText
            text="Review your responses, how they were graded, and any feedback provided."
            style={{ color: "#667085" }}
            weight="normal"
          />
        )}
        {gradingData?.hasTeacherAdjustedGrade && (
          <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
            <CustomText text="The teacher has adjusted the grade." style={{ color: "#667085" }} weight="normal" />
          </View>
        )}
      </View>
      <CustomSpacing size="xl" />
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      ) : (
        <>
          <View style={styles.studentInfo}>
            <View style={styles.infoItem}>
              <CustomText text="name" size="s" weight="ultraBold" style={{ color: "#344054" }} />
              <CustomText
                text={gradingData?.userName || ""}
                size="s"
                useTranslationText={false}
                style={{ color: "#344054" }}
              />
            </View>
            <View style={styles.infoItem}>
              <CustomText text="date_taken" size="s" weight="ultraBold" style={{ color: "#344054" }} />
              <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                <CustomText
                  text={new Date(gradingData?.startedAt || "").toLocaleDateString()}
                  size="s"
                  useTranslationText={false}
                  style={{ color: "#344054" }}
                  weight="normal"
                />
                <CustomText text="at" size="s" />
                <CustomText
                  text={new Date(gradingData?.startedAt || "").toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  size="s"
                  useTranslationText={false}
                  style={{ color: "#344054" }}
                  weight="normal"
                />
              </View>
            </View>
            <View style={styles.infoItem}>
              <CustomText text="total_score" size="s" weight="ultraBold" style={{ color: "#344054" }} />
              {canEditScore ? (
                <>
                  <CustomTextInput
                    value={gradeTree?.totalPoints || ""}
                    onChangeText={async (text) => {
                      handleGradeChange({
                        updatedEntityType: UpdatedEntityType.Submission,
                        updatedEntityId: gradeTree.id,
                        updatedGrade: text,
                        maxPointsForEntity: gradeTree.maximumPoints,
                      });
                    }}
                    placeholder="Enter total score"
                    style={{
                      backgroundColor: "white",
                      borderRadius: 8,
                      paddingHorizontal: 6,
                      paddingVertical: 4,
                      width: 60,
                    }}
                    onBlur={() => handleSave(false)}
                  />
                  <CustomText
                    text={`/${gradeTree?.maximumPoints}`}
                    size="s"
                    useTranslationText={false}
                    style={{ color: "#344054" }}
                  />
                </>
              ) : (
                <CustomText
                  text={`${gradeTree?.totalPoints}/${gradeTree?.maximumPoints}`}
                  size="s"
                  useTranslationText={false}
                  style={{ color: "#344054" }}
                  weight="normal"
                />
              )}
            </View>
            <View style={styles.infoItem}>
              <CustomText text="time_spent" size="s" weight="ultraBold" style={{ color: "#344054" }} />
              <CustomText
                text={calculateTimeSpent()}
                size="s"
                useTranslationText={false}
                style={{ color: "#344054" }}
                weight="normal"
              />
            </View>
          </View>
          <CustomSpacing size="xl" />

          <View style={styles.container}>
            <View style={styles.questionsContainer}>
              {gradingData?.questions?.map((question, questionIndex) => {
                const gradeTreeQuestion = gradeTree?.questions?.find((q) => q.id === question.id);

                return (
                  <View
                    key={questionIndex}
                    style={{
                      paddingLeft: 10,
                      paddingRight: 32,
                      borderWidth: 1,
                      borderColor: "#EAECF0",
                      borderRadius: 16,
                    }}>
                    <View style={styles.accordionContainer}>
                      <View style={styles.accordionActions}>
                        <View style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 4 }}>
                          <CustomText text="score_earned" size="s" weight="bold" style={{ color: "#667085" }} />
                        </View>
                        <View style={styles.scorePill}>
                          {canEditScore ? (
                            <>
                              <CustomTextInput
                                value={gradeTreeQuestion?.totalPoints || ""}
                                onChangeText={async (text) => {
                                  handleGradeChange({
                                    updatedEntityType: UpdatedEntityType.Question,
                                    updatedEntityId: gradeTreeQuestion.id,
                                    updatedGrade: text,
                                    maxPointsForEntity: gradeTreeQuestion.maximumPoints,
                                  });
                                }}
                                placeholder="Enter total score"
                                onBlur={() => handleSave(false)}
                              />
                              <CustomText
                                text={`/${gradeTreeQuestion?.maximumPoints}`}
                                size="s"
                                useTranslationText={false}
                                style={{ color: "#344054" }}
                              />
                            </>
                          ) : (
                            <CustomText
                              text={`${gradeTreeQuestion?.totalPoints}/${gradeTreeQuestion?.maximumPoints}`}
                              size="s"
                              style={{ color: "#326212" }}
                              weight="bold"
                              useTranslationText={false}
                            />
                          )}
                        </View>
                      </View>
                      <CustomAccordion
                        isExpanded={false}
                        title={
                          <View style={styles.accordionHeader}>
                            <View style={{ paddingRight: 50, flex: 1 }}>
                              <Text>
                                <Text style={styles.questionTextTitle}>{`Question ${questionIndex + 1}: `}</Text>
                                <Text style={styles.questionText}>{question.question}</Text>
                              </Text>
                            </View>
                          </View>
                        }>
                        <View style={styles.divider} />
                        <CustomSpacing size="l" />
                        <View style={styles.answerSection}>
                          <CustomText
                            text="student_response"
                            size="s"
                            weight="ultraBold"
                            style={{ color: "#101828" }}
                          />
                          <CustomText
                            text={question.userAnswer?.answer || ""}
                            size="s"
                            useTranslationText={false}
                            style={{ color: "#101828" }}
                            weight="normal"
                          />
                        </View>
                        <CustomSpacing size="m" />
                        <View style={styles.answerSection}>
                          <CustomText
                            text="grading_criteria"
                            size="s"
                            weight="ultraBold"
                            style={{ color: "#101828" }}
                          />
                          {question.rubrics?.map((rubric, rubricIndex) => {
                            const numStars = Math.min(Math.round((rubric.pointsAwarded / rubric.maxPoints) * 4), 4);
                            const gradeTreeRubric = gradeTreeQuestion?.rubrics?.find((r) => r.id === rubric.id);

                            return (
                              <View key={rubricIndex} style={styles.accordionContainer}>
                                <View style={styles.accordionActions}>
                                  {Array.from({ length: numStars }).map((_, starIndex) => (
                                    <CustomText text="⭐" size="s" useTranslationText={false} key={starIndex} />
                                  ))}
                                  <View style={styles.scorePill}>
                                    {canEditScore ? (
                                      <>
                                        <CustomTextInput
                                          value={gradeTreeRubric?.pointsAwarded || ""}
                                          onChangeText={async (text) => {
                                            handleGradeChange({
                                              updatedEntityType: UpdatedEntityType.Rubric,
                                              updatedEntityId: gradeTreeRubric.id,
                                              updatedGrade: text,
                                              maxPointsForEntity: gradeTreeRubric.maxPoints,
                                            });
                                          }}
                                          placeholder="Enter total score"
                                          onBlur={() => handleSave(false)}
                                        />
                                        <CustomText
                                          text={`/${gradeTreeRubric?.maxPoints}`}
                                          size="s"
                                          useTranslationText={false}
                                          style={{ color: "#344054" }}
                                        />
                                      </>
                                    ) : (
                                      <CustomText
                                        text={`(${gradeTreeRubric?.pointsAwarded}/${gradeTreeRubric?.maxPoints} points)`}
                                        size="s"
                                        style={{ color: "#101828" }}
                                        weight="normal"
                                        useTranslationText={false}
                                      />
                                    )}
                                  </View>
                                </View>
                                <CustomAccordion
                                  isExpanded={false}
                                  title={
                                    <View
                                      style={{
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 4,
                                        padding: 0,
                                      }}>
                                      <CustomText
                                        text={rubric.component || ""}
                                        size="s"
                                        useTranslationText={false}
                                        style={{ color: "#101828" }}
                                        weight="normal"
                                      />
                                    </View>
                                  }>
                                  <CustomText
                                    text={rubric.remarks || ""}
                                    size="s"
                                    useTranslationText={false}
                                    style={{ color: "#101828" }}
                                    weight="normal"
                                  />
                                </CustomAccordion>
                              </View>
                            );
                          })}
                        </View>
                      </CustomAccordion>
                    </View>
                  </View>
                );
              })}
            </View>
          </View>
        </>
      )}
    </>
  );
};

export default SubmissionAndGradingTab;

const styles = StyleSheet.create({
  paddingContainer: {
    paddingHorizontal: 32,
  },
  studentInfo: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    backgroundColor: "#F2F4F7",
    borderRadius: 4,
    marginHorizontal: 32,
    paddingHorizontal: 12,
    paddingVertical: 4,
    gap: 20,
  },
  infoItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  scorePill: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: "360px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    maxWidth: 100,
  },
  container: {
    flex: 1,
    overflow: "scroll",
    marginHorizontal: 32,
  },
  questionsContainer: {
    gap: 12,
    width: "100%",
  },
  accordionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  questionTextTitle: {
    fontFamily: "Inter",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "700",
    color: "#101828",
  },
  questionText: {
    fontFamily: "Inter",
    fontSize: 14,
    lineHeight: 20,
    fontWeight: "400",
    color: "#101828",
  },
  accordionActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 5,
  },
  answerSection: {
    gap: 8,
    paddingVertical: 4,
    paddingHorizontal: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300,
    minWidth: "100%",
    width: "100%",
    paddingHorizontal: 32,
  },
  divider: {
    height: 1,
    backgroundColor: "#EAECF0",
    marginHorizontal: 16,
  },
  accordionContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "row-reverse",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
});
